// @djsem/ui — Electron Studio App Shell model (Canonical UI Prompt 81).
//
// Pure, framework-free logic for the desktop shell: the primary navigation model with
// permission/plan gating, the command-palette registry + fuzzy filter, and the bottom
// status-bar derivation. React components render this model; keeping it pure makes the
// shell behavior fully testable without Electron.
//
// Authority: prompts 00–80 own the business rules consumed here (permissions from 28,
// entitlements from 03B, rights counts from 65, jobs from 30, sync from 29, safe-mode
// from 23R/56). This module only decides what the SHELL shows.

/**
 * Plan tier vocabulary used by the shell for gating (5 values).
 *
 * Structurally identical to @djsem/shared's DJSEMPlanTier (aiProviderRegistry.ts) — that
 * package already provides planLevelToProviderPlanTier() for collapsing the real 7-tier
 * DJSEMPlanLevel onto this same 5-value vocabulary; workspaceApp.tsx reuses it directly
 * rather than duplicating the mapping here.
 */
export type DJSEMPlanTierUI = "free" | "creator" | "creator_pro" | "performer" | "enterprise";

const PLAN_RANK: Record<DJSEMPlanTierUI, number> = {
  free: 0, creator: 1, creator_pro: 2, performer: 3, enterprise: 4,
};

/** Id of a primary or admin navigation module. */
export type DJSEMNavModuleId =
  | "dashboard" | "library" | "playlists" | "crates" | "sets"
  | "visuals" | "brand" | "create" | "stems"
  | "assets" | "releases" | "distribution" | "campaigns" | "drops"
  | "gigs" | "live" | "feedback" | "jobs" | "marketplace" | "settings"
  | "admin" | "support" | "moderation";

/** A single navigation-item definition (before per-user resolution). */
export interface DJSEMNavItem {
  id: DJSEMNavModuleId;
  label: string;
  /** Permission required to see this item at all (admin-only items). */
  requiredPermission?: string;
  /** Minimum plan; below it the item renders plan-locked (visible but gated). */
  minPlan?: DJSEMPlanTierUI;
  adminOnly?: boolean;
  /** Optional grouping for section headers in the sidebar. */
  group?: "core" | "creative" | "release" | "gig" | "system";
}

/** The canonical primary navigation (prompt 81 §5 + master guardrail §9). Order matters. */
export const PRIMARY_NAV: readonly DJSEMNavItem[] = [
  // Core music workflow — the guardrail's central journey.
  { id: "dashboard", label: "Dashboard", group: "core" },
  { id: "library",   label: "Library",   group: "core" },
  { id: "playlists", label: "Playlists", group: "core" },
  { id: "crates",    label: "Crates",    group: "core" },
  { id: "sets",      label: "Sets",      group: "core" },
  // Creative — visuals + brand world for the music.
  { id: "visuals",   label: "Visuals",   group: "creative" },
  { id: "brand",     label: "Brand World", group: "creative" },
  { id: "create",    label: "Create",    minPlan: "creator", group: "creative" },
  { id: "stems",     label: "Stem Studio", minPlan: "creator", group: "creative" },
  // Release + distribution.
  { id: "assets",       label: "Assets",       group: "release" },
  { id: "releases",     label: "Releases",     group: "release" },
  { id: "distribution", label: "Distribution", minPlan: "creator_pro", group: "release" },
  { id: "campaigns",    label: "Campaigns",    group: "release" },
  { id: "drops",        label: "Gated Drops",  group: "release" },
  // Gig + live.
  { id: "gigs",      label: "Gigs",      group: "gig" },
  { id: "live",      label: "Live",      minPlan: "performer", group: "gig" },
  { id: "feedback",  label: "Set Learning", group: "gig" },
  // System.
  { id: "jobs",      label: "Jobs",      group: "system" },
  { id: "marketplace", label: "Marketplace", group: "system" },
  { id: "settings",  label: "Settings",  group: "system" },
] as const;

/** Admin-only navigation (prompt 81 §5). Hidden entirely without the permission. */
export const ADMIN_NAV: readonly DJSEMNavItem[] = [
  { id: "admin", label: "Admin", adminOnly: true, requiredPermission: "admin:console" },
  { id: "support", label: "Support", adminOnly: true, requiredPermission: "support:read" },
  { id: "moderation", label: "Moderation", adminOnly: true, requiredPermission: "support:moderate" },
] as const;

/** The current user's plan + permissions, used to resolve nav/command gating. */
export interface ShellUserContext {
  plan: DJSEMPlanTierUI;
  permissions: string[];
}

/** Resolved visibility of a nav item for the current user. */
export type NavItemState = "enabled" | "plan_locked" | "hidden";

/** A nav item after gating has been resolved for the current user. */
export interface ResolvedNavItem extends DJSEMNavItem {
  state: NavItemState;
}

/**
 * Resolve nav visibility per prompt 81 §5: hide admin items without permission; render
 * plan-gated items as visible-but-locked (so upgrade paths are discoverable, per 03B).
 * @param user - The current user's plan + permissions.
 * @returns Primary nav items (enabled or plan-locked) followed by any permitted admin items.
 */
export function resolveNavigation(user: ShellUserContext): ResolvedNavItem[] {
  const out: ResolvedNavItem[] = [];
  for (const item of PRIMARY_NAV) {
    const locked = item.minPlan !== undefined && PLAN_RANK[user.plan] < PLAN_RANK[item.minPlan];
    out.push({ ...item, state: locked ? "plan_locked" : "enabled" });
  }
  for (const item of ADMIN_NAV) {
    const allowed = item.requiredPermission !== undefined && user.permissions.includes(item.requiredPermission);
    if (allowed) out.push({ ...item, state: "enabled" });
    // Admin items without permission are HIDDEN, not locked (prompt 81 §5).
  }
  return out;
}

// ---- Command palette (prompt 81 §8) ----

/** A single command-palette entry. */
export interface DJSEMPaletteCommand {
  id: string;
  title: string;
  /** Module the command navigates to or acts within. */
  module: DJSEMNavModuleId;
  keywords?: string[];
  requiredPermission?: string;
  minPlan?: DJSEMPlanTierUI;
}

/** Core command registry: navigation + the flows the addendum requires to be reachable. */
export const CORE_COMMANDS: readonly DJSEMPaletteCommand[] = [
  { id: "nav.dashboard", title: "Go to Dashboard", module: "dashboard" },
  { id: "nav.library", title: "Go to Library", module: "library", keywords: ["tracks", "music"] },
  { id: "nav.playlists", title: "Go to Playlists", module: "playlists", keywords: ["import", "spotify", "soundcloud"] },
  { id: "nav.crates", title: "Go to Crates", module: "crates", keywords: ["smart", "camelot", "filter"] },
  { id: "nav.sets", title: "Go to Sets", module: "sets" },
  { id: "nav.visuals", title: "Go to Visuals", module: "visuals", keywords: ["scenes", "lighting", "cues"] },
  { id: "nav.brand", title: "Go to Brand World", module: "brand", keywords: ["identity", "colors", "fonts"] },
  { id: "nav.create", title: "Go to Create", module: "create", minPlan: "creator" },
  { id: "nav.assets", title: "Go to Assets", module: "assets", keywords: ["artwork", "social"] },
  { id: "nav.releases", title: "Go to Releases", module: "releases", keywords: ["studio", "epk"] },
  { id: "nav.distribution", title: "Go to Distribution", module: "distribution", keywords: ["distrokid", "tunecore", "submission"], minPlan: "creator_pro" },
  { id: "nav.campaigns", title: "Go to Campaigns", module: "campaigns", keywords: ["pitch", "smart link"] },
  { id: "nav.drops", title: "Go to Gated Drops", module: "drops", keywords: ["competition", "giveaway", "fan", "unlock"] },
  { id: "nav.gigs", title: "Go to Gigs", module: "gigs" },
  { id: "nav.live", title: "Go to Live", module: "live", minPlan: "performer" },
  { id: "nav.feedback", title: "Go to Set Learning", module: "feedback", keywords: ["soundcloud", "youtube", "audience", "feedback", "retention", "recommendations"] },
  { id: "nav.jobs", title: "Go to Jobs", module: "jobs" },
  { id: "nav.settings", title: "Go to Settings", module: "settings" },
  { id: "library.import", title: "Import local music", module: "library", keywords: ["add", "files", "upload"] },
  { id: "library.import.rekordbox", title: "Import Rekordbox XML", module: "library", keywords: ["xml", "pioneer"] },
  { id: "library.import.playlist", title: "Import Spotify/SoundCloud playlist", module: "library", keywords: ["spotify", "soundcloud"] },
  { id: "sets.new", title: "Build a new set", module: "sets", keywords: ["setlist"] },
  { id: "sets.coach", title: "Run AI Set Coach", module: "sets", keywords: ["critique"], minPlan: "creator" },
  { id: "create.artwork", title: "Generate artwork", module: "create", keywords: ["ai", "image"], minPlan: "creator" },
  { id: "nav.stems", title: "Go to Stem Studio", module: "stems", keywords: ["stem", "separation", "demucs", "vocals", "instrumental"], minPlan: "creator" },
  { id: "gigs.brief", title: "Create event brief", module: "gigs" },
  { id: "gigs.pack", title: "Create gig pack", module: "gigs" },
  { id: "live.rehearsal", title: "Prepare rehearsal", module: "live", minPlan: "performer" },
  { id: "jobs.failed", title: "Review failed jobs", module: "jobs" },
  { id: "settings.rights", title: "Manage rights & provenance", module: "settings", keywords: ["ledger", "license"] },
  { id: "settings.privacy.export", title: "Export my data", module: "settings", keywords: ["dsar", "download"] },
  { id: "admin.moderation", title: "Open moderation queue", module: "moderation", requiredPermission: "support:moderate" },
] as const;

/**
 * Filter palette commands for a query + user. Simple case-insensitive subsequence match
 * over title + keywords; permission-hidden; plan-locked commands remain listed but flagged
 * (discoverable upgrade path). Deterministic ordering: exact-prefix first, then by title.
 * @param query - The user's palette search text.
 * @param user - The current user's plan + permissions.
 * @param registry - The command registry to search; defaults to {@link CORE_COMMANDS}.
 * @returns Matching commands (permission-hidden ones excluded), each flagged with `planLocked`.
 */
export function filterCommands(
  query: string,
  user: ShellUserContext,
  registry: readonly DJSEMPaletteCommand[] = CORE_COMMANDS,
): Array<DJSEMPaletteCommand & { planLocked: boolean }> {
  const q = query.trim().toLowerCase();
  const matches = (cmd: DJSEMPaletteCommand): boolean => {
    if (q.length === 0) return true;
    const hay = [cmd.title, ...(cmd.keywords ?? [])].join(" ").toLowerCase();
    // subsequence match
    let i = 0;
    for (const ch of hay) { if (ch === q[i]) i++; if (i === q.length) return true; }
    return hay.includes(q);
  };
  return registry
    .filter((cmd) => !cmd.requiredPermission || user.permissions.includes(cmd.requiredPermission))
    .filter(matches)
    .map((cmd) => ({
      ...cmd,
      planLocked: cmd.minPlan !== undefined && PLAN_RANK[user.plan] < PLAN_RANK[cmd.minPlan],
    }))
    .sort((a, b) => {
      const ap = a.title.toLowerCase().startsWith(q) ? 0 : 1;
      const bp = b.title.toLowerCase().startsWith(q) ? 0 : 1;
      return ap - bp || a.title.localeCompare(b.title);
    });
}

// ---- Bottom status bar derivation (prompt 81 §7) ----

/** Raw inputs used to derive the bottom status-bar segments. */
export interface ShellStatusInput {
  online: boolean;
  cachedPackageCount: number;
  syncQueueLength: number;
  activeJobCount: number;
  storageWarning?: string;
  rightsWarningCount: number;
  liveState: "idle" | "rehearsal" | "live";
  safeModeActive: boolean;
  blackoutActive?: boolean;
  version: string;
  channel?: "dev" | "beta" | "stable";
}

/** A single rendered status-bar segment. */
export interface ShellStatusSegment {
  id: string;
  text: string;
  tone: "neutral" | "ok" | "warn" | "danger";
}

/**
 * Derive the ordered status-bar segments. Safety states always render and outrank cosmetics.
 * @param input - Current shell status inputs.
 * @returns The ordered segments to render in the bottom status bar.
 */
export function deriveStatusBar(input: ShellStatusInput): ShellStatusSegment[] {
  const segments: ShellStatusSegment[] = [];
  segments.push({ id: "net", text: input.online ? "online" : "offline (cache only)", tone: input.online ? "ok" : "warn" });
  segments.push({ id: "cache", text: `${input.cachedPackageCount} cached`, tone: "neutral" });
  if (input.syncQueueLength > 0) segments.push({ id: "sync", text: `${input.syncQueueLength} pending sync`, tone: "warn" });
  if (input.activeJobCount > 0) segments.push({ id: "jobs", text: `${input.activeJobCount} job${input.activeJobCount === 1 ? "" : "s"} running`, tone: "neutral" });
  if (input.storageWarning) segments.push({ id: "storage", text: input.storageWarning, tone: "warn" });
  if (input.rightsWarningCount > 0) segments.push({ id: "rights", text: `${input.rightsWarningCount} rights warning${input.rightsWarningCount === 1 ? "" : "s"}`, tone: "warn" });
  if (input.liveState !== "idle") segments.push({ id: "live", text: input.liveState === "live" ? "LIVE" : "rehearsal", tone: input.liveState === "live" ? "danger" : "warn" });
  if (input.safeModeActive) segments.push({ id: "safe", text: "SAFE MODE", tone: "danger" });
  if (input.blackoutActive) segments.push({ id: "blackout", text: "BLACKOUT", tone: "danger" });
  segments.push({ id: "ver", text: `v${input.version}${input.channel && input.channel !== "stable" ? ` (${input.channel})` : ""}`, tone: "neutral" });
  return segments;
}
